import React, { useEffect, useState } from 'react';
import { Chips, SideSheet } from '@gerdau/hefesto-mais-web';
import { Skeleton, Button, Dialog } from '@gerdau/hefesto';
import {
  BoldText,
  SkeletonContainer,
  TextContainer,
  ChipsContainer,
  ButtonsContainer,
  ActionContainer,
  Container,
  CancelButtonContainer,
  WarningText
} from './styles';

import VolumeUpdate from './VolumeUpdate';
import CancelUpdate from './CancelUpdate';
import DateUpdate from './DateUpdate';
import OtherUpdate from './OtherUpdate';
import {
  isInvalidMultipleValue,
  isLoweThanMinimumQty,
  isOutOfMinimunDate,
  isWeekend,
  separateTermsWithPunctuation
} from '../../../../../../Purchase/LongSteel/OrderSettings/ShoppingCartTable/shoppingCartFieldsValidation';
import moment, { Moment } from 'moment';
import { IUserOrderItemPreference } from '../../../../../../../store/ducks/user/types';
import {
  IOpenOrderQuantities,
  IUpdateOrderRequest,
  IUpdateOrderResponse
} from '../../../../../../../store/ducks/long-steel-orders/types';
import { IMeasureUnit } from '../../../../../../../store/ducks/long-steel-shopping-cart/types';
import { LongSteelOrdersService } from '../../../../../../../services/LongSteelOrders';
import { addToast } from '../../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../../store/ducks/toast/types';
import { useDispatch } from 'react-redux';
import { IOpenOrdersTableDataSource } from '..';
import {
  addBusinessDays,
  countErrors,
  formatChangeValidationData,
  formatOnRequestError,
  formatRequestBody,
  formatSelectedItens,
  groupErrors,
  mergeGroupedErrors
} from './utils';
import { AxiosError } from 'axios';
import { makeid } from '../../../../../../../utils/formatStrings';
import { sendTagging } from '../../../../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../../../../store/ducks/tagging/types';

interface IUpdateOrder {
  tableSelectedItems: IOpenOrdersTableDataSource[];
  onClose: () => void;
  userOrderPreference: IUserOrderItemPreference;
  clearSelectItems: () => void;
}

export type ICantUpdateItems = Record<ValidationErrorEnum, string[]>;

export interface ISelectedTableOrders {
  salesOrder: string;
  orderNumber: string;
  materials: IMaterialSelected[];
}

export interface IMaterialSelected {
  id: React.Key;
  description: string;
  orderedQuantity: IOpenOrderQuantities;
  measurement: IMeasureUnit | undefined;
  requestedDate: Moment;
  newVolume?: string;
  newDate?: string;
  customerId: string;
  changeAllowed: boolean;
  reasonNotAllowed?: ValidationErrorEnum;
  salesOrderItem: string;
  salesOrder: string;
  errorType?: UpdateErrorType;
  updateErrorMessage?: string;
}

export interface IRequestError {
  description: string;
  reason: string;
}

export enum UpdateErrorType {
  DATE = 'requiredDate',
  QUANTITY = 'quantity',
  STATUS = 'status',
  ORDER_TYPE = 'orderType/quantities',
  ITEM = 'item'
}

export enum ValidationErrorEnum {
  CANCEL = 'O item já possui uma solicitação de cancelamento',
  SHIPPING = 'O item já se encontra em Remessa',
  TRANSPORT = 'O item já se encontra em Transporte',
  INVOICING = 'O item já se encontra em Faturamento Parcial',
  DEFAULT = 'O item não pôde ser validado'
}

export const UpdateErrorMsgMap = new Map<UpdateErrorType, string>();
UpdateErrorMsgMap.set(
  UpdateErrorType.DATE,
  'já existe um ajuste de data em andamento.'
);
UpdateErrorMsgMap.set(
  UpdateErrorType.QUANTITY,
  'já existe um ajuste de volume em andamento.'
);
UpdateErrorMsgMap.set(
  UpdateErrorType.STATUS,
  'já existe um cancelamento em processo.'
);
UpdateErrorMsgMap.set(
  UpdateErrorType.ORDER_TYPE,
  'não é Aço Longo ou possui transfer/faturamento'
);

const UpdateOrderSideSheet = ({
  onClose,
  tableSelectedItems,
  userOrderPreference,
  clearSelectItems
}: IUpdateOrder) => {
  const dispatch = useDispatch();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [loadingUpdate, setLoadingUpdatade] = useState(false);
  const [validatingItems, setValidatingItems] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [otherMessage, setOtherMessage] = useState('');
  const [cantUpdate, setCantUpdate] = useState<ICantUpdateItems>(
    {} as ICantUpdateItems
  );
  const [option, setOption] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<ISelectedTableOrders[]>(
    []
  );
  const [canShowDialog, setCanShowDialog] = useState(false);
  const [hasUpdateError, setHasUpdateError] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const validateItems = async () => {
      try {
        setValidatingItems(true);
        const [validationItems, dataItems] =
          formatChangeValidationData(tableSelectedItems);

        const validatedItemsPromise =
          LongSteelOrdersService.validationToUpdate(validationItems);

        const itemsDataPromise =
          LongSteelOrdersService.changeOrderData(dataItems);

        const [validatedItems, itemsData] = await Promise.all([
          validatedItemsPromise,
          itemsDataPromise
        ]);

        setSelectedItems(
          formatSelectedItens(
            tableSelectedItems,
            validatedItems,
            itemsData,
            userOrderPreference
          )
        );
      } catch (err) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Falha ao carregar os items selecionados',
            durationInMS: 5000
          })
        );
        onClose();
        document.body.style.overflow = 'unset';
      } finally {
        setValidatingItems(false);
      }
    };
    validateItems();

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const cantUpdateItems: ICantUpdateItems[] = [];
    selectedItems.forEach((order) => {
      cantUpdateItems.push(groupErrors(order.materials));
    });
    const mergedErrors = cantUpdateItems.reduce(
      mergeGroupedErrors,
      {} as ICantUpdateItems
    );
    setCantUpdate(mergedErrors);

    return () => {
      setCantUpdate({} as ICantUpdateItems);
    };
  }, [selectedItems]);

  const chipsChoice = [
    {
      label: 'Ajuste de Volume',
      id: 'volumeChip'
    },
    {
      label: 'Ajuste da Data Solicitada',
      id: 'deliveryChip'
    },
    {
      label: 'Cancelamento',
      id: 'cancelChip'
    },
    {
      label: 'Outro',
      id: 'otherChip'
    }
  ];

  const renderValidationError = (): JSX.Element[] | null => {
    const entries = Object.entries(cantUpdate);
    return entries.length
      ? entries.map((value) => {
          const message = value[0].split(/\b(em)|\b(uma)/gi);
          const materials = value[1];
          return (
            <WarningText key={`${value[0]}`}>
              <p>
                Infelizmente {materials.length > 1 ? 'os itens ' : 'o item '}
                {materials.map((item, index) => {
                  return (
                    <span key={makeid(6)}>
                      <BoldText>{item}</BoldText>
                      {separateTermsWithPunctuation(
                        index,
                        materials.length,
                        ' '
                      )}
                    </span>
                  );
                })}
                {materials.length > 1
                  ? ` não podem ser alterados pois os itens já se encontram em${
                      message[message.length - 1]
                    }.`
                  : ` não pode ser alterado pois ${value[0].toLocaleLowerCase()}.`}
              </p>
            </WarningText>
          );
        })
      : null;
  };

  const renderOption = () => {
    if (option?.length) {
      switch (option[0]) {
        case 'volumeChip':
          return (
            <VolumeUpdate
              selectedItens={selectedItems}
              onChangeValue={onChangeField}
              loading={loadingUpdate}
              warningComponents={renderValidationError()}
              hasUpdateError={hasUpdateError}
            />
          );
        case 'cancelChip':
          return (
            <CancelUpdate
              disableTextArea={
                loadingUpdate ||
                countErrors(cantUpdate) === selectedItems.length
              }
              message={cancelMessage}
              setMessage={onChangeReason}
              showErrorMessage={errorMessage}
              warningComponents={renderValidationError()}
              hasUpdateError={hasUpdateError}
              selectedItens={selectedItems}
            />
          );
        case 'deliveryChip':
          return (
            <DateUpdate
              selectedItens={selectedItems}
              onChangeDate={onChangeField}
              warningComponents={renderValidationError()}
              hasUpdateError={hasUpdateError}
            />
          );
        case 'otherChip':
          return (
            <OtherUpdate
              loading={loadingUpdate}
              otherReason={otherMessage}
              setOtherReason={onChangeReason}
              errorMessage={errorMessage}
              hasUpdateError={hasUpdateError}
              selectedItens={selectedItems}
            />
          );
        default:
          return null;
      }
    }
    return null;
  };

  const onCloseSideSheet = () => {
    if (showSuccessMessage) {
      dispatch(
        sendTagging(
          prepareTagging(
            'button:solicitacaofechar',
            'solicitacaosucesso_fechar'
          )
        )
      );
      document.body.style.overflow = 'unset';
      setOption([]);
      onClose();
      return;
    }
    setCanShowDialog(true);
  };

  const onDialogConfirm = () => {
    dispatch(
      sendTagging(
        prepareTagging('button:alteracaofechar', 'solicitaralteracao_fechar')
      )
    );
    clearSelectItems();
    setCanShowDialog(false);
    document.body.style.overflow = 'unset';
    setOption([]);
    onClose();
  };

  const prepareTagging = (
    itemName: string,
    itemDescription: string
  ): ITaggingParams => {
    return {
      screenName: 'LG:PainelGestao:Selecao',
      event: 'click',
      itemName,
      itemDescription
    };
  };

  const onClickSend = async () => {
    setLoadingUpdatade(true);
    let requestBody: IUpdateOrderRequest[] = [];
    let tagBody;
    switch (option[0]) {
      case 'volumeChip':
        tagBody = prepareTagging(
          'button:ajustedevolume',
          'solicitaralteracao_ajustedevolume'
        );
        requestBody = formatRequestBody(selectedItems, 'volumeChip');
        break;
      case 'cancelChip':
        tagBody = prepareTagging(
          'button:cancelamento',
          'solicitaralteracao_cancelamento'
        );
        if (cancelMessage.trim().length === 0) {
          setErrorMessage(
            'Preencha o campo acima para dar continuidade a sua solicitação'
          );
          setLoadingUpdatade(false);
          return;
        }
        requestBody = formatRequestBody(
          selectedItems,
          'cancelChip',
          cancelMessage
        );
        setErrorMessage('');
        setCancelMessage('');
        break;
      case 'deliveryChip':
        tagBody = prepareTagging(
          'button:ajustededatasolicitada',
          'solicitaralteracao_ajustededatasolicitada'
        );
        requestBody = formatRequestBody(selectedItems, 'deliveryChip');
        break;
      case 'otherChip':
        tagBody = prepareTagging('button:outro', 'solicitaralteracao_outro');
        if (otherMessage.trim().length === 0) {
          setErrorMessage(
            'Preencha o campo acima para dar continuidade a sua solicitação'
          );
          setLoadingUpdatade(false);
          return;
        }
        requestBody = formatRequestBody(
          selectedItems,
          'otherChip',
          undefined,
          otherMessage
        );
        setErrorMessage('');
        setOtherMessage('');
        break;
      default:
        return;
    }
    dispatch(sendTagging(tagBody));
    LongSteelOrdersService.sendOrderUpdateData(requestBody)
      .then((response) => {
        if (
          response?.every((item) => item.requestEditStatus === 'processando')
        ) {
          setShowSuccessMessage(true);
        }
      })
      .catch((error: AxiosError<{ items: IUpdateOrderResponse[] }>) => {
        if (error.response?.status === 422) {
          setHasUpdateError(true);
          setSelectedItems(
            formatOnRequestError(selectedItems, error.response.data.items)
          );
        } else {
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message: 'Aconteceu um erro inesperado. Tente novamente'
            })
          );
        }
      })
      .finally(() => setLoadingUpdatade(false));
  };

  const onClickCloseSucces = () => {
    dispatch(
      sendTagging(
        prepareTagging('button:solicitacaofechar', 'solicitacaosucesso_fechar')
      )
    );
    setShowSuccessMessage(false);
    onClose();
  };

  const onChangeReason = (msg: string, option: string): void => {
    if (errorMessage.length) {
      setErrorMessage('');
    }
    if (option === 'other') {
      setOtherMessage(msg);
      return;
    }
    setCancelMessage(msg);
  };

  const isVolumeInvalid = (): boolean => {
    const checks: boolean[] = [];
    for (const order of selectedItems) {
      checks.push(
        order.materials.some(
          (material) =>
            material.newVolume === undefined ||
            material.newVolume === '' ||
            material.newVolume === '0.000' ||
            (material.measurement &&
              isInvalidMultipleValue(
                material.measurement,
                material.newVolume
              )) ||
            (material.measurement &&
              isLoweThanMinimumQty(material.measurement, material.newVolume))
        )
      );
    }
    return checks.every((item) => item === false);
  };

  const isDateInvalid = (): boolean => {
    const checks: boolean[] = [];
    for (const order of selectedItems) {
      checks.push(
        order.materials.some(
          (material) =>
            material.newDate === undefined ||
            material.newDate === '' ||
            isOutOfMinimunDate(addBusinessDays(3), material.newDate) ||
            isWeekend(moment(material.newDate))
        )
      );
    }
    return checks.every((item) => item === false);
  };

  const shouldDisableSendBtn = (): boolean => {
    const opt = option[0];
    const error = materialRetunedError(opt);
    if (option.length === 0 || loadingUpdate) {
      return true;
    } else if (opt === 'volumeChip') {
      return !isVolumeInvalid() || error;
    } else if (opt === 'deliveryChip') {
      return !isDateInvalid() || error;
    } else if (opt === 'cancelChip') {
      return countErrors(cantUpdate) === selectedItems.length || error;
    } else if (opt === 'otherChip') {
      return error;
    }
    return false;
  };

  const materialRetunedError = (option: string): boolean => {
    const materials = selectedItems.flatMap((item) => item.materials);
    if (option === 'deliveryChip') {
      return materials.some(
        (m) =>
          m.errorType === UpdateErrorType.DATE ||
          m.errorType === UpdateErrorType.ORDER_TYPE ||
          m.errorType === UpdateErrorType.ITEM
      );
    } else if (option === 'volumeChip') {
      return materials.some(
        (m) =>
          m.errorType === UpdateErrorType.QUANTITY ||
          m.errorType === UpdateErrorType.ORDER_TYPE ||
          m.errorType === UpdateErrorType.ITEM
      );
    }
    return materials.some(
      (m) =>
        m.errorType === UpdateErrorType.STATUS ||
        m.errorType === UpdateErrorType.ORDER_TYPE
    );
  };

  const onChangeField = (
    value: string,
    itemId: string,
    orderId: string,
    key: 'newVolume' | 'newDate'
  ) => {
    const orders = [...selectedItems];
    const orderIndex = orders.findIndex((item) => item.orderNumber === orderId);
    if (orderIndex !== -1) {
      const materialIndex = orders[orderIndex].materials.findIndex(
        (material) => material.id === itemId
      );
      if (materialIndex !== -1) {
        orders[orderIndex].materials[materialIndex][key] = value;
        setSelectedItems(orders);
      }
    }
  };

  const formatHeaderText = (selectedItems: ISelectedTableOrders[]) => {
    return selectedItems.map((order, index) => (
      <span key={order.orderNumber}>
        <BoldText>nᵒ {order.orderNumber}</BoldText>
        <span> com </span>
        <BoldText>
          {order.materials.length}
          {order.materials.length > 1 ? ' itens' : ' item'}
        </BoldText>
        {separateTermsWithPunctuation(index, selectedItems.length, '?')}
      </span>
    ));
  };

  const renderSideSheetBody = () => {
    return showSuccessMessage ? (
      <>
        <TextContainer>
          {option[0] === 'cancelChip' ? (
            <p>
              A solicitação de cancelamento foi enviada com sucesso. Logo um dos
              nossos colaboradores entrará em contato com você.
            </p>
          ) : (
            <p>
              Sucesso! Logo um dos nossos colaboradores entrará em contato com
              você.
            </p>
          )}
          <p>
            Lembrando que podemos levar até 3 dias úteis para responder a sua
            solicitação.
          </p>
        </TextContainer>
        <ButtonsContainer>
          <Button name="btnClose" onClick={onClickCloseSucces} fullWidth>
            Fechar
          </Button>
        </ButtonsContainer>
      </>
    ) : (
      <>
        <TextContainer cy-data-test="updateOrder-text-container">
          <p data-testid="headerText">
            O que precisamos alterar{' '}
            {selectedItems.length > 1 ? 'nos seus pedidos' : 'no seu pedido'}
            {': '}
            {formatHeaderText(selectedItems)}
          </p>
          <p>
            Escolha o que você deseja alterar, logo vamos trabalhar no seu
            pedido.
          </p>
        </TextContainer>
        <ChipsContainer>
          <Chips
            type="choice"
            chips={chipsChoice}
            handleSelect={(selectedChipIds: string[]) => {
              if (errorMessage.length) {
                setErrorMessage('');
              }
              setOption(selectedChipIds);
            }}
            disabled={loadingUpdate}
          />
        </ChipsContainer>
        <ActionContainer>{renderOption()}</ActionContainer>
        <ButtonsContainer>
          {option[0] === 'cancelChip' ? (
            <>
              <CancelButtonContainer>
                <Button
                  name="Do not want to cancel btn"
                  loading={loadingUpdate}
                  variant="primary"
                  disabled={loadingUpdate}
                  onClick={onClose}
                  fullWidth
                >
                  Não quero cancelar
                </Button>
              </CancelButtonContainer>
              <CancelButtonContainer>
                <Button
                  name="Want to cancel btn"
                  loading={loadingUpdate}
                  variant="secondary"
                  disabled={loadingUpdate || shouldDisableSendBtn()}
                  onClick={onClickSend}
                  fullWidth
                >
                  Sim, quero cancelar
                </Button>
              </CancelButtonContainer>
            </>
          ) : (
            <Button
              name="btnSend"
              disabled={shouldDisableSendBtn()}
              variant="primary"
              loading={loadingUpdate}
              fullWidth
              onClick={onClickSend}
            >
              Enviar
            </Button>
          )}
        </ButtonsContainer>
      </>
    );
  };

  return (
    <Container>
      <SideSheet
        title="Solicitar alteração no pedido"
        openFromSide="right"
        onClose={onCloseSideSheet}
        isOpen={true}
      >
        {validatingItems ? (
          <SkeletonContainer data-testid="skeleton-container">
            <Skeleton />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
            <Skeleton type="title" />
          </SkeletonContainer>
        ) : (
          renderSideSheetBody()
        )}
        {canShowDialog && (
          <Dialog
            actions={{
              right: {
                name: 'Confirm btn',
                children: 'Sim, cancelar',
                onClick: () => onDialogConfirm()
              },
              left: {
                name: 'Cancel btn',
                children: 'Não, continuar',
                onClick: () => setCanShowDialog(false)
              }
            }}
            textContent="Deseja cancelar a alteração de pedido?"
          />
        )}
      </SideSheet>
    </Container>
  );
};

export default UpdateOrderSideSheet;
